import React, { useMemo, useState } from "react";
import { BarChart3, CircleDot, GitBranch, RotateCcw } from "lucide-react";
import ActivationReportPanel from "./ActivationReportPanel";
import ProgressionFunnelPanel from "./ProgressionFunnelPanel";
import StartCompletionReportPanel from "./StartCompletionReportPanel";
import RetentionReportPanel from "./RetentionReportPanel";

const REPORTS = {
  FUNNEL: "funnel",
  ACTIVATION: "activation",
  START_COMPLETION: "start_completion",
  RETENTION: "retention",
};

const ReportsTab = ({ paidOnly = false }) => {
  const [activeReport, setActiveReport] = useState(REPORTS.FUNNEL);

  const reportOptions = useMemo(
    () => [
      {
        id: REPORTS.FUNNEL,
        label: "Progression Funnel",
        description: "Signup, login, and module completion movement.",
        icon: GitBranch,
      },
      {
        id: REPORTS.ACTIVATION,
        label: "Activation",
        description: "Signed-up users who have not started.",
        icon: CircleDot,
      },
      {
        id: REPORTS.START_COMPLETION,
        label: "Start / Completion",
        description: "Started-but-stale users and section outcomes.",
        icon: BarChart3,
      },
      {
        id: REPORTS.RETENTION,
        label: "Retention",
        description: "Return cohorts and dormant users.",
        icon: RotateCcw,
      },
    ],
    []
  );

  const activeOption = reportOptions.find((option) => option.id === activeReport);

  return (
    <div className="grid grid-cols-1 gap-5 xl:grid-cols-[300px_minmax(0,1fr)]">
      <aside className="xl:sticky xl:top-4 xl:self-start">
        <div className="rounded-[28px] border border-slate-200 bg-white p-3 shadow-[0_10px_35px_rgba(15,23,42,0.06)]">
          <div className="px-2 pb-3 pt-1">
            <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
              Report View
            </div>
            <div className="mt-1 text-sm text-slate-600">
              {paidOnly ? "Filtered to paid users" : "Showing all users"}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-1">
            {reportOptions.map((option) => {
              const Icon = option.icon;
              const active = option.id === activeReport;

              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setActiveReport(option.id)}
                  className={`rounded-2xl border p-3 text-left transition ${
                    active
                      ? "border-[#54B0AF] bg-[#eefafa] shadow-sm"
                      : "border-transparent bg-slate-50/70 hover:border-slate-200 hover:bg-white"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <span
                      className={`mt-0.5 rounded-xl p-2 ${
                        active
                          ? "bg-[#54B0AF] text-white"
                          : "bg-white text-slate-500"
                      }`}
                    >
                      <Icon size={18} />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-slate-900">
                        {option.label}
                      </span>
                      <span className="mt-1 block text-xs leading-5 text-slate-500">
                        {option.description}
                      </span>
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </aside>

      <div className="min-w-0 space-y-4">
        <div className="rounded-[28px] border border-slate-200 bg-white px-5 py-4 shadow-[0_10px_35px_rgba(15,23,42,0.05)]">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.16em] text-[#54B0AF]">
                Active Report
              </div>
              <h2 className="mt-1 text-xl font-semibold text-slate-900">
                {activeOption?.label}
              </h2>
            </div>
            <div className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-600">
              {paidOnly ? "Paid users only" : "All users"}
            </div>
          </div>
        </div>

        {activeReport === REPORTS.FUNNEL && (
          <ProgressionFunnelPanel paidOnly={paidOnly} />
        )}
        {activeReport === REPORTS.ACTIVATION && (
          <ActivationReportPanel paidOnly={paidOnly} />
        )}
        {activeReport === REPORTS.START_COMPLETION && (
          <StartCompletionReportPanel paidOnly={paidOnly} />
        )}
        {activeReport === REPORTS.RETENTION && (
          <RetentionReportPanel paidOnly={paidOnly} />
        )}
      </div>
    </div>
  );
};

export default ReportsTab;

